import { SvgIconComponent } from "@mui/icons-material";
import {
  CalendarMonthRounded,
  CompareArrowsRounded,
  TrackChangesRounded,
} from "@mui/icons-material";

/**
 * Starter prompts for the chat landing.
 *
 * These are PURE UI. A preset is just a string handed to the same `onStart`
 * the composer calls, so the first message is byte-identical to one the user
 * typed. No preset ids, no seeded conversations, nothing on the server that
 * has to be kept in step with this file. Editing the copy here is the entire
 * deployment story, and the API cannot tell the difference.
 */
export interface ChatPreset {
  /** Sent verbatim as the first message. */
  prompt: string;
  icon: SvgIconComponent;
}

/**
 * Keep every prompt ANSWERABLE. A preset that returns "I couldn't find that"
 * is worse than no preset: it is a live demo of the product failing, on the
 * first screen, to a user deciding whether to keep paying. Prefer large,
 * well-covered companies and topics the tools genuinely reach.
 */

/**
 * One card is drawn from each group on every visit.
 *
 * Plain fundamentals were dropped from the draw entirely (Ben, 2026-07-31:
 * "Break down Costco's valuation multiples" is "very basic"). Valuation and
 * cash-flow questions are answered well, they just do not persuade anyone, and
 * a card that reads like every other finance app wastes the slot. They keep
 * their section in ChatCapabilitiesSheet, where the job is completeness rather
 * than a first impression.
 */
const SUPPORTING_POOL: ChatPreset[][] = [
  [
    {
      prompt: "Show me the price target for Rocket Lab",
      icon: TrackChangesRounded,
    },
    {
      prompt: "What are analysts' price targets for Nebius?",
      icon: TrackChangesRounded,
    },
  ],
  [
    {
      prompt: "Show me Apple's revenue chart over five years",
      icon: CompareArrowsRounded,
    },
    {
      prompt: "Compare Microsoft and Alphabet's margins",
      icon: CompareArrowsRounded,
    },
  ],
  [
    {
      prompt: "What's on the economic calendar this week?",
      icon: CalendarMonthRounded,
    },
    {
      prompt: "When does Amazon next report earnings?",
      icon: CalendarMonthRounded,
    },
  ],
];

const pickOne = <T>(items: T[]): T =>
  items[Math.floor(Math.random() * items.length)];

/**
 * One prompt from each group, shuffled.
 *
 * Shuffling matters: without it each capability would sit in the same
 * position on every visit, which reads as a fixed menu rather than a rotating
 * one.
 *
 * Call this from a `useState` initialiser, never in the render body. It must
 * stay fixed for the life of the screen (a fresh draw on every keystroke would
 * make the cards flicker) while still changing each time the user returns.
 */
export const pickPresets = (): ChatPreset[] => {
  const chosen = SUPPORTING_POOL.map(pickOne);
  for (let i = chosen.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chosen[i], chosen[j]] = [chosen[j], chosen[i]];
  }
  return chosen;
};
